import { useEffect, useMemo, useState } from 'react';
import { classesApi, cursusApi, gradingApi, gradesApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChartBar, Trophy, Warning, GraduationCap, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import WorkloadAlerts from '../components/director/WorkloadAlerts';
import BackToSchoolAssistant from '../components/director/BackToSchoolAssistant';
import AlertsFeed from '../components/director/AlertsFeed';
import BulletinSignatureCampaign from '../components/director/BulletinSignatureCampaign';
import PushToggleCard from '../components/dashboard/PushToggleCard';

const PERIOD_OPTIONS = {
  trimestre: [
    { value: 'trimestre-1', label: '1er trimestre' },
    { value: 'trimestre-2', label: '2è trimestre' },
    { value: 'trimestre-3', label: '3è trimestre' },
  ],
  semestre: [
    { value: 'semestre-1', label: '1er semestre' },
    { value: 'semestre-2', label: '2è semestre' },
  ],
  periode: [
    { value: 'periode-1', label: '1ère période' },
    { value: 'periode-2', label: '2è période' },
    { value: 'periode-3', label: '3è période' },
    { value: 'periode-4', label: '4è période' },
    { value: 'periode-5', label: '5è période' },
    { value: 'periode-6', label: '6è période' },
    { value: 'examen-1', label: '1er examen' },
    { value: 'examen-2', label: '2è examen' },
    { value: 'examen-3', label: '3è examen' },
  ],
};

const currentSchoolYear = () => {
  const now = new Date();
  const y = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-${y + 1}`;
};

const DirectorDashboard = () => {
  const { t } = useLanguage();
  const [classes, setClasses] = useState([]);
  const [classConfigs, setClassConfigs] = useState([]);
  const [className, setClassName] = useState('');
  const [periodKey, setPeriodKey] = useState('');
  const [schoolYear, setSchoolYear] = useState(currentSchoolYear());
  const [computed, setComputed] = useState(null);
  const [cursus, setCursus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [defaultOutcome, setDefaultOutcome] = useState('promoted');
  const [downloadingClassPdf, setDownloadingClassPdf] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([classesApi.getAll(), gradingApi.classConfigs()]);
        setClasses(a.data || []);
        setClassConfigs(b.data || []);
        if ((a.data || []).length && !className) {
          const first = a.data[0].name || a.data[0];
          setClassName(first);
        }
      } catch { /* noop */ }
    })();
  }, []); // eslint-disable-line

  const classCfg = useMemo(
    () => classConfigs.find((c) => c.class_name === className) || null,
    [classConfigs, className],
  );

  const periodOptions = useMemo(
    () => (classCfg ? (PERIOD_OPTIONS[classCfg.period_type] || []) : []),
    [classCfg],
  );

  // Auto-reset period when class changes
  useEffect(() => {
    setPeriodKey('');
    setComputed(null);
  }, [className]);

  // Default-pick the first available period
  useEffect(() => {
    if (periodOptions.length && !periodKey) setPeriodKey(periodOptions[0].value);
  }, [periodOptions, periodKey]);

  const reload = async () => {
    if (!className || !periodKey || !schoolYear) return;
    setLoading(true);
    try {
      const [g, c] = await Promise.all([
        gradesApi.computed(className, periodKey, schoolYear).then((r) => r.data).catch(() => null),
        cursusApi.byClass(className, schoolYear).then((r) => r.data).catch(() => []),
      ]);
      setComputed(g);
      setCursus(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [className, periodKey, schoolYear]);

  // ---- Derived KPIs (from gradesApi.computed) ----
  const stats = useMemo(() => {
    if (!computed) return null;
    const cs = computed.class_stats || {};
    const ranked = (computed.students || [])
      .filter((s) => s.general_average != null)
      .sort((a, b) => (a.general_rank || 999) - (b.general_rank || 999));
    const top = ranked.slice(0, 3).map((s) => ({
      student_id: s.student_id,
      student_name: `${s.last_name} ${s.first_name}`.trim(),
      average: s.general_average,
      rank: s.general_rank,
    }));
    const struggling = ranked
      .filter((s) => (s.general_average || 0) < 10)
      .map((s) => ({
        student_id: s.student_id,
        student_name: `${s.last_name} ${s.first_name}`.trim(),
        average: s.general_average,
        rank: s.general_rank,
      }));
    const passing = ranked.filter((s) => (s.general_average || 0) >= 10).length;
    const total = ranked.length;
    // Median
    const sorted = [...ranked].sort((a, b) => a.general_average - b.general_average);
    const median = sorted.length
      ? Math.round((sorted[Math.floor(sorted.length / 2)].general_average) * 100) / 100
      : null;
    return {
      count: cs.count_with_grades,
      count_total: cs.count_total,
      class_average: cs.avg,
      median,
      top,
      struggling,
      passing_rate: total ? Math.round((100 * passing) / total) : null,
    };
  }, [computed]);

  const downloadClassPdf = async () => {
    if (!className || !periodKey) return;
    setDownloadingClassPdf(true);
    try {
      const res = await gradesApi.bulletinsClassPdf(className, periodKey, schoolYear);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletins_${className}_${periodKey}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally {
      setDownloadingClassPdf(false);
    }
  };

  const promoteAll = async () => {
    const outcomeLabel = defaultOutcome === 'promoted' ? t('promoted')
      : defaultOutcome === 'repeated' ? t('repeated') : defaultOutcome;
    const msg = t('confirmPromote')
      .replace('{class}', className)
      .replace('{year}', schoolYear)
      .replace('{outcome}', outcomeLabel);
    if (!window.confirm(msg)) return;
    setPromoting(true);
    try {
      const { data } = await cursusApi.promoteClass({
        class_name: className, school_year: schoolYear, default_outcome: defaultOutcome,
      });
      toast.success(`${data.affected} ${t('studentsAffected')}`);
      reload();
    } catch { toast.error(t('error')); } finally { setPromoting(false); }
  };

  const classOptions = useMemo(
    () => classes.map((c) => (typeof c === 'string' ? c : (c.name || c.class_name || ''))).filter(Boolean),
    [classes],
  );

  return (
    <div className="space-y-6" data-testid="director-dashboard">
      <div className="flex items-center gap-3">
        <GraduationCap size={28} className="text-[#0052FF]" weight="duotone" />
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('directorDashboard')}</h1>
      </div>

      {/* Push notifications toggle — director_studies receives period lock,
          workload alert and admin broadcast push notifications. */}
      <PushToggleCard />

      <AlertsFeed />

      {/* Filters */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>{t('classLabel')}</Label>
            <select
              className="w-full border border-[#E2E8F0] rounded-md p-2 text-sm"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              data-testid="director-class-select"
            >
              <option value="">{t('pickClass')}</option>
              {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>
              {t('pickPeriod')}
              {classCfg && <span className="ml-2 text-xs text-[#64748B]">({classCfg.period_type})</span>}
            </Label>
            <select
              className="w-full border border-[#E2E8F0] rounded-md p-2 text-sm"
              value={periodKey}
              onChange={(e) => setPeriodKey(e.target.value)}
              disabled={!classCfg}
              data-testid="director-period-select"
            >
              <option value="">{classCfg ? '—' : 'Choisir une classe d\'abord'}</option>
              {periodOptions.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <Label>{t('schoolYear')}</Label>
            <Input
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              placeholder="2025-2026"
              data-testid="director-year-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Back-to-school readiness checklist */}
      <BackToSchoolAssistant schoolYear={schoolYear} />

      {/* Workload alerts (weekly hours + missing principals) */}
      <WorkloadAlerts schoolYear={schoolYear} />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="director-kpis">
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#64748B]">{t('studentsScored')}</p>
            <p className="font-outfit text-2xl font-bold">{stats?.count ?? '—'}</p>
            {stats?.count_total != null && (
              <p className="text-[10px] text-[#94A3B8] mt-0.5">/ {stats.count_total} inscrits</p>
            )}
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#64748B]">{t('classAverage')}</p>
            <p className="font-outfit text-2xl font-bold text-[#0052FF]">{stats?.class_average ?? '—'}/20</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#64748B]">{t('median')}</p>
            <p className="font-outfit text-2xl font-bold">{stats?.median ?? '—'}/20</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#64748B]">{t('passingRate')}</p>
            <p className={`font-outfit text-2xl font-bold ${(stats?.passing_rate ?? 0) >= 50 ? 'text-green-600' : 'text-red-600'}`}>
              {stats?.passing_rate ?? '—'}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top + Struggling */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="director-top-students">
          <CardHeader>
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <Trophy size={18} className="text-amber-500" weight="duotone" /> {t('top3')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.top || []).length === 0 ? (
              <p className="text-sm text-[#94A3B8]">{t('noBulletinsForPeriod')}</p>
            ) : (
              <ol className="space-y-2 text-sm">
                {stats.top.map((s, i) => (
                  <li key={s.student_id} className="flex items-center justify-between p-2 rounded bg-amber-50">
                    <span className="flex items-center gap-2">
                      <span className="font-bold text-amber-700">#{s.rank ?? (i + 1)}</span>
                      {s.student_name}
                    </span>
                    <span className="font-bold">{s.average}/20</span>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>

        <Card className="border border-[#E2E8F0] shadow-none" data-testid="director-struggling">
          <CardHeader>
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <Warning size={18} className="text-red-500" weight="duotone" /> {t('struggling')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.struggling || []).length === 0 ? (
              <p className="text-sm text-green-600">{t('noStrugglingStudents')}</p>
            ) : (
              <ul className="space-y-1 text-sm max-h-56 overflow-auto">
                {stats.struggling.map((s) => (
                  <li
                    key={s.student_id}
                    className="flex items-center justify-between py-1 border-b border-[#F1F5F9] last:border-0"
                  >
                    <span>{s.student_name}</span>
                    <span className="font-bold text-red-600">{s.average}/20</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bulletins of the period (class PDF export) */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <ChartBar size={18} className="text-[#0052FF]" />
            Bulletins de la période
          </CardTitle>
          <Button
            variant="outline"
            disabled={!className || !periodKey || downloadingClassPdf || !computed?.students?.length}
            onClick={downloadClassPdf}
            className="border-[#B45309] text-[#B45309] hover:bg-[#FEF3C7]"
            data-testid="director-class-bulletins-pdf"
          >
            <FilePdf size={16} className="mr-2" />
            {downloadingClassPdf ? '…' : 'Télécharger tous les bulletins (PDF)'}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-4 text-sm">{t('loading')}</p>
          ) : !computed?.students?.length ? (
            <p className="p-4 text-sm text-[#94A3B8]">Aucune note encore saisie pour cette période.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                <tr>
                  <th className="text-left p-2">{t('rankLabel')}</th>
                  <th className="text-left p-2">{t('studentLabel')}</th>
                  <th className="text-left p-2">{t('avgLabel')}</th>
                </tr>
              </thead>
              <tbody>
                {[...computed.students]
                  .filter((s) => s.general_average != null)
                  .sort((a, b) => (a.general_rank || 999) - (b.general_rank || 999))
                  .map((s) => (
                    <tr
                      key={s.student_id}
                      className="border-b border-[#F1F5F9]"
                      data-testid={`director-student-row-${s.student_id}`}
                    >
                      <td className="p-2 font-medium">
                        {s.general_rank ?? '—'}
                        {computed.class_stats?.count_with_grades
                          ? `/${computed.class_stats.count_with_grades}`
                          : ''}
                      </td>
                      <td className="p-2">{s.last_name} {s.first_name}</td>
                      <td className={`p-2 font-bold ${(s.general_average || 0) >= 10 ? 'text-green-600' : 'text-red-600'}`}>
                        {s.general_average}/20
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* iter68 — Bulletin Signature Campaign */}
      <BulletinSignatureCampaign
        className={className}
        periodKey={periodKey}
        schoolYear={schoolYear}
      />

      {/* Year-end class promotion */}
      <Card className="border border-[#0052FF] shadow-none" data-testid="promote-card">
        <CardHeader>
          <CardTitle className="font-outfit text-base">{t('yearEndPromotion')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#64748B]">
            {t('yearEndPromotionDesc')}{' '}
            <span className="font-bold">{className || '—'}</span> ·{' '}
            <span className="font-bold">{schoolYear}</span>
          </p>
          <div className="flex gap-2 items-end">
            <div>
              <Label>{t('defaultDecision')}</Label>
              <select
                value={defaultOutcome}
                onChange={(e) => setDefaultOutcome(e.target.value)}
                className="border border-[#E2E8F0] rounded-md p-2 text-sm"
                data-testid="promote-outcome-select"
              >
                <option value="promoted">{t('promoted')}</option>
                <option value="repeated">{t('repeated')}</option>
                <option value="transferred">{t('transferred')}</option>
                <option value="left">{t('leftSchool')}</option>
              </select>
            </div>
            <Button
              onClick={promoteAll}
              disabled={promoting || !className}
              data-testid="promote-btn"
            >
              {promoting ? t('loading') : t('applyToWholeClass')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cursus snapshot of the class for the year */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit text-base">
            {t('cursusForClass')} {className} — {schoolYear} ({cursus.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {cursus.length === 0 ? (
            <p className="p-4 text-sm text-[#94A3B8]">{t('noCursusEntries')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                <tr>
                  <th className="text-left p-2">{t('studentLabel')}</th>
                  <th className="text-left p-2">{t('avgLabel')}</th>
                  <th className="text-left p-2">{t('decisionLabel')}</th>
                  <th className="text-left p-2">{t('commentShort')}</th>
                </tr>
              </thead>
              <tbody>
                {cursus.map((c) => (
                  <tr
                    key={c.student_id}
                    className="border-b border-[#F1F5F9]"
                    data-testid={`cursus-row-${c.student_id}`}
                  >
                    <td className="p-2">{c.student_name}</td>
                    <td className={`p-2 font-bold ${
                      (c.average || 0) >= 10
                        ? 'text-green-600'
                        : c.average == null
                        ? 'text-[#94A3B8]'
                        : 'text-red-600'
                    }`}>
                      {c.average != null ? `${c.average}/20` : '—'}
                    </td>
                    <td className="p-2">
                      {c.outcome ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.outcome === 'promoted' ? 'bg-green-100 text-green-700'
                          : c.outcome === 'repeated' ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-700'
                        }`}>{c.outcome}</span>
                      ) : <span className="text-[#94A3B8]">—</span>}
                    </td>
                    <td className="p-2 text-xs text-[#64748B] truncate max-w-xs">{c.comment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DirectorDashboard;
